import React, { useState, useEffect } from 'react';
import CommonHeader from '../CommonHeader';
import { StatusBadge, DataTable, ColumnDef, Modal } from '../shared';
import { formatDate, formatCurrency } from '../../utils';
import { apiUrl } from '@/config/api';

interface Order {
  id: string;
  order_number?: string; // Add order_number field
  customer_name?: string;
  customer_phone?: string;
  order_type?: string;
  garmentType?: string; // Add garmentType field
  cloth_type?: string;
  price?: number;
  amount?: number; // Add amount field
  status: 'pending' | 'in_progress' | 'ready' | 'completed' | 'cancelled';
  priority?: 'high' | 'medium' | 'low';
  due_date?: string;
  pickup_date?: string;
  assigned_tailor?: string;
  measurements?: {
    neck?: number;
    chest?: number;
    waist?: number;
    sleeve_length?: number;
    inseam?: number;
    [key: string]: number | undefined;
  };
  special_notes?: string;
  created_date?: string;
  last_updated?: string;
}

const CustomerOrderManagement: React.FC = () => {
  console.log('📋 CustomerOrderManagement component rendered');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Real order data from API
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);

  // Load orders from API
  useEffect(() => {
    const loadOrders = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setOrders([]);
          setIsLoadingOrders(false);
          return;
        }

        const response = await fetch(apiUrl('/api/orders'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Map backend field names to frontend expected field names
          const mappedOrders = (data.orders || []).map((order: any) => ({
            ...order,
            // Normalize order number from different casings
            order_number: order.order_number || order.orderNumber,
            order_type: order.garmentType || order.order_type,
            due_date: order.dueDate || order.due_date,
            created_date: order.orderDate || order.created_date || order.created_at
          }));
          setOrders(mappedOrders);
        } else {
          console.log('Failed to load orders:', response.status);
          setOrders([]);
        }
      } catch (_error) {
        console.log('Error loading orders:', error);
        setOrders([]);
      } finally {
        setIsLoadingOrders(false);
      }
    };

    loadOrders();
  }, []);

  const getDaysUntilDue = (dueDate: string) => {
    if (!dueDate) return 0;
    try {
      const due = new Date(dueDate);
      if (isNaN(due.getTime())) return 0;
      const today = new Date();
      const diffTime = due.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (_error) {
      console.log('Error parsing date:', dueDate, error);
      return 0;
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = (order.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (order.customer_phone || '').includes(searchTerm) ||
                         (order.id || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (order.order_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && order.priority !== priorityFilter) return false;
    
    return true;
  });

  // Table columns
  const columns: ColumnDef<Order>[] = [
    {
      key: 'id',
      header: 'Order ID',
      render: (value, order) => (
        <div className="flex items-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-lg mr-3">
            {order.order_number ? order.order_number.split('-')[2] || order.order_number : value}
          </div>
          <div>
            <div className="font-semibold text-gray-900">{order.order_number || `#${value}`}</div>
            <div className="text-sm text-gray-600">{order.created_date ? formatDate(order.created_date) : 'N/A'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'customer_name',
      header: 'Customer',
      render: (value, order) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-600">{order.customer_phone}</div>
        </div>
      )
    },
    {
      key: 'order_type',
      header: 'Order Details',
      render: (value, order) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-600">{order.cloth_type}</div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <StatusBadge 
          status={value} 
          type="order" 
          persona="customer"
          showIcon={true}
        />
      )
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (value) => (
        <StatusBadge 
          status={value} 
          type="priority" 
          persona="customer"
        />
      )
    },
    {
      key: 'due_date',
      header: 'Due Date',
      render: (value, order) => {
        if (!value) return <span className="text-gray-400">No date set</span>;
        
        const daysUntilDue = getDaysUntilDue(value);
        const isOverdue = daysUntilDue < 0;
        
        return (
          <div>
            <div className={`font-medium ${isOverdue ? 'text-red-600' : ''}`}>
              {formatDate(value)}
            </div>
            {isOverdue && (
              <div className="text-xs text-red-600">
                {Math.abs(daysUntilDue)} days overdue
              </div>
            )}
            {!isOverdue && daysUntilDue > 0 && (
              <div className="text-xs text-gray-500">
                {daysUntilDue} days left
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (value) => formatCurrency(value || 0)
    }
  ];

  const actions = [
    { label: 'View Timeline', action: 'timeline', variant: 'primary' as const },
    { label: 'View Details', action: 'details', variant: 'secondary' as const },
    { label: 'Message Tailor', action: 'message', variant: 'secondary' as const }
  ];

  const handleAction = (action: string, row: Order) => {
    switch (action) {
      case 'timeline':
        // Navigate to timeline
        window.location.href = `/order/${row.order_number || row.id}/details`;
        break;
      case 'details':
        setSelectedOrder(row);
        break;
      case 'message':
        // Open message modal
        break;
    }
  };

  const renderOrderDetails = (order: Order) => {
    const daysUntilDue = getDaysUntilDue(order.due_date);
    const isOverdue = daysUntilDue < 0;

    return (
      <Modal
        isOpen={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        title={`Order Details - ${order.order_number || order.id}`}
        size="lg"
      >
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Customer Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{order.customer_name || 'Not Available'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium">{order.customer_phone || 'Not Available'}</p>
              </div>
            </div>
          </div>

          {/* Order Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Order Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Order Type</p>
                <p className="font-medium">{order.garmentType || order.order_type || 'Not Available'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Cloth Type</p>
                <p className="font-medium">{order.cloth_type || 'Not Available'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Price</p>
                <p className="font-medium">{formatCurrency(order.amount || order.price || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <StatusBadge 
                  status={order.status} 
                  type="order" 
                  persona="customer"
                  showIcon={true}
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">Priority</p>
                <StatusBadge 
                  status={order.priority} 
                  type="priority" 
                  persona="customer"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">Due Date</p>
                <p className={`font-medium ${isOverdue ? 'text-red-600' : ''}`}>
                  {formatDate(order.due_date)}
                  {isOverdue && ` (${Math.abs(daysUntilDue)} days overdue)`}
                </p>
              </div>
            </div>
          </div>

          {/* Measurements */}
          {Object.keys(order.measurements).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Measurements (inches)</h3>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(order.measurements).map(([key, value]) => (
                  <div key={key}>
                    <p className="text-sm text-gray-600 capitalize">{key.replace('_', ' ')}</p>
                    <p className="font-medium">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Photos */}
          {order.photos && order.photos.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Order Photos</h3>
              <div className="grid grid-cols-3 gap-4">
                {order.photos.map((photo: any) => (
                  <div key={photo.id} className="aspect-square bg-gray-200 rounded-lg overflow-hidden">
                    <img
                      src={photo.photo_url || photo.thumbnail_url || photo.url}
                      alt={`${photo.photo_type || 'Photo'} view`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {order.special_notes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Special Notes</h3>
              <p className="text-gray-700">{order.special_notes}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Track Progress
            </button>
            <button className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              Message Tailor
            </button>
            <button 
              onClick={() => setSelectedOrder(null)}
              className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CommonHeader 
        title="My Orders"
        showBackButton={true}
        backTo="/customer/dashboard"
      >
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">
            {orders.length} orders
          </span>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <span className="mr-2">➕</span>
            New Order
          </button>
        </div>
      </CommonHeader>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Orders</label>
                <input
                  type="text"
                  placeholder="Search by order ID, tailor, or garment type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="ready">Ready for Pickup</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Priority</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <DataTable
          data={filteredOrders}
          columns={columns}
          persona="customer"
          searchable={false} // We have custom search above
          filterable={false} // We have custom filters above
          pagination={true}
          sortable={true}
          actions={actions}
          onAction={handleAction}
          emptyMessage="No orders found. Try adjusting your search terms or create a new order."
        />

        {/* Summary Stats */}
        {filteredOrders.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{orders.length}</div>
                  <div className="text-sm text-gray-600">Total Orders</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {orders.filter(o => o.status === 'pending' || o.status === 'in_progress').length}
                  </div>
                  <div className="text-sm text-gray-600">Active Orders</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {orders.filter(o => o.status === 'ready').length}
                  </div>
                  <div className="text-sm text-gray-600">Ready for Pickup</div>
                </div>
                <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">
                    {formatCurrency(orders.reduce((sum, o) => sum + (o.amount ?? o.price ?? 0), 0))}
                  </div>
                  <div className="text-sm text-gray-600">Total Value</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && renderOrderDetails(selectedOrder)}

      {/* Create Order Modal Placeholder */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create New Order"
          size="md"
        >
          <div className="text-center">
            <div className="text-4xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Create New Order</h3>
            <p className="text-gray-600 mb-6">Order creation form coming soon...</p>
            <button
              onClick={() => setShowCreateModal(false)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CustomerOrderManagement;
